import React, { useCallback, useMemo, useState } from 'react';
import {
  Badge, Box, Button, Group, Modal, Paper, Select, Stack, Switch, Tabs, Text,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { themeStylesOf } from '../../../shared/theme/themeStyles';
import { readThemeConfiguration } from '../../../shared/theme/themeConfiguration';
import { CUSTOM_STYLE_KEY, DOCUMENT_STYLE, DOCUMENT_STYLE_KINDS } from '../../../shared/theme/styleMapping';
import ElementStylePreview from '../../theme/ElementStylePreview';

const KIND_LABELS = {
  table: 'Tableaux',
  infoCard: 'Cartes info',
  accordion: 'Volets',
};
// Valeur de select pour « style du document » : Mantine n'accepte pas null.
const DOCUMENT_OPTION = '__document';

// Garder le style du document : l'importer, ou, pour une carte ou un volet
// personnalisés, les laisser tels quels.
const documentAnswer = (entry) => (entry.key === CUSTOM_STYLE_KEY ? null : DOCUMENT_STYLE);
const isDocumentAnswer = (value) => value == null || value === DOCUMENT_STYLE;

function initialAnswers(plan) {
  return Object.fromEntries(DOCUMENT_STYLE_KINDS.map((kind) => [kind, { ...plan[kind].mapping }]));
}

function mappingSections({ plan, used, theme }, showAll) {
  return DOCUMENT_STYLE_KINDS.map((kind) => {
    const webStyles = themeStylesOf(readThemeConfiguration(theme), kind);
    const keys = showAll
      ? Object.keys(plan[kind].mapping).filter((key) => !plan[kind].imports.includes(key))
      : plan[kind].questions;
    const entries = keys.map((key) => used.entries[kind].find((entry) => entry.key === key));
    return { kind, webStyles, entries };
  }).filter(({ entries, webStyles }) => entries.length && webStyles.length);
}

const countEntries = (sections) => sections.reduce((sum, { entries }) => sum + entries.length, 0);

// Largeur à laquelle un aperçu est rendu, puis réduit pour tenir dans sa case :
// à sa largeur réelle, un tableau déborde et se coupe sur un écran étroit.
const PREVIEW_WIDTH = 520;

function PreviewSlot({ label, children }) {
  const { ref, width } = useElementSize();
  const scale = width ? Math.min(1, width / PREVIEW_WIDTH) : 1;
  return (
    <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Box
        ref={ref}
        p="xs"
        bg="gray.0"
        style={{ borderRadius: 6, overflow: 'hidden', pointerEvents: 'none' }}
      >
        <div style={{ width: PREVIEW_WIDTH, zoom: scale }}>{children}</div>
      </Box>
    </Stack>
  );
}

/**
 * Quel style du thème du site prend chaque style du document, en avant /
 * après : l'élément rendu avec le style du document, puis avec le style du
 * site choisi. Ne liste que les styles à trancher (nouveaux, orphelins, ou
 * plusieurs candidats) ; l'interrupteur montre aussi ceux déjà associés. Un
 * onglet par type d'élément quand il y en a plusieurs.
 *
 * Chaque ligne a déjà une réponse proposée : valider suffit. Annuler arrête la
 * génération (`onCancel`).
 */
export function StyleMappingModal({ request, onConfirm, onCancel }) {
  const {
    plan, used, theme, documentConfiguration = {},
  } = request;
  const siteConfiguration = useMemo(() => readThemeConfiguration(theme), [theme]);
  const [answers, setAnswers] = useState(() => initialAnswers(plan));
  const questionCount = useMemo(() => countEntries(mappingSections(request, false)), [request]);
  // Rien à trancher : la modale est ouverte pour revoir les choix déjà faits.
  const [showAll, setShowAll] = useState(questionCount === 0);
  const [activeKind, setActiveKind] = useState(null);

  const sections = useMemo(() => mappingSections(request, showAll), [request, showAll]);
  const alreadyMappedCount = useMemo(
    () => countEntries(mappingSections(request, true)) - questionCount,
    [request, questionCount],
  );
  const activeSection = sections.find(({ kind }) => kind === activeKind) || sections[0];

  const answer = (kind, key, value) => setAnswers((prev) => ({
    ...prev,
    [kind]: { ...prev[kind], [key]: value },
  }));

  const selectValue = (kind, key) => {
    const current = answers[kind][key];
    return isDocumentAnswer(current) ? DOCUMENT_OPTION : String(current);
  };

  const answerOf = (webStyles, entry, value) => {
    const style = webStyles.find((s) => String(s.id) === value);
    return style ? style.id : documentAnswer(entry);
  };

  const styleOptions = (webStyles) => [
    { value: DOCUMENT_OPTION, label: 'Garder celui du document' },
    ...webStyles.map((style) => ({ value: String(style.id), label: style.name })),
  ];

  // Toutes les lignes, y compris celles réglées sans question, gardent le
  // style du document.
  const keepAllDocumentStyles = () => onConfirm(Object.fromEntries(DOCUMENT_STYLE_KINDS.map((kind) => [
    kind,
    {
      ...answers[kind],
      ...Object.fromEntries(used.entries[kind]
        .filter((entry) => !plan[kind].imports.includes(entry.key))
        .map((entry) => [entry.key, documentAnswer(entry)])),
    },
  ])));

  const answerWholeSection = ({ kind, webStyles, entries }, value) => setAnswers((prev) => ({
    ...prev,
    [kind]: {
      ...prev[kind],
      ...Object.fromEntries(entries.map((entry) => [entry.key, answerOf(webStyles, entry, value)])),
    },
  }));

  const documentPreview = (kind, entry) => (entry.style
    ? (
      <ElementStylePreview
        kind={kind}
        styleId={entry.style.id}
        configuration={documentConfiguration}
        theme={theme}
      />
    )
    : <Text size="xs" c="dimmed">Chaque bloc garde ses propres réglages.</Text>);

  const renderEntry = (kind, webStyles, entry) => {
    const current = answers[kind][entry.key];
    const proposed = current === plan[kind].mapping[entry.key];
    return (
      <Paper key={entry.key} withBorder p="sm" radius="md">
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={600}>{entry.name}</Text>
          <Text size="xs" c="dimmed">
            {entry.count}
            {' '}
            {entry.count > 1 ? 'blocs' : 'bloc'}
          </Text>
        </Group>
        <Group align="stretch" wrap="nowrap" gap="md">
          <PreviewSlot label="Dans le document">{documentPreview(kind, entry)}</PreviewSlot>
          <Box c="dimmed" style={{ alignSelf: 'center', fontSize: 28 }}>
            <FontAwesomeIcon icon={icon({ name: 'arrow-right', style: 'light' })} />
          </Box>
          <PreviewSlot label="Sur le site">
            {isDocumentAnswer(current)
              ? documentPreview(kind, entry)
              : (
                <ElementStylePreview
                  kind={kind}
                  styleId={current}
                  configuration={siteConfiguration}
                  theme={theme}
                />
              )}
          </PreviewSlot>
        </Group>
        <Group justify="flex-end" gap="xs" mt="xs">
          {proposed && <Badge size="sm" variant="light" color="gray">Proposé</Badge>}
          <Select
            size="xs"
            w={240}
            aria-label={`Style du site pour ${entry.name}`}
            data={styleOptions(webStyles)}
            value={selectValue(kind, entry.key)}
            onChange={(value) => {
              if (value == null) return;
              answer(kind, entry.key, answerOf(webStyles, entry, value));
            }}
            allowDeselect={false}
          />
        </Group>
      </Paper>
    );
  };

  const renderSection = (section) => {
    const { kind, webStyles, entries } = section;
    return (
      <Stack gap="xs">
        {entries.length > 1 && (
          <Group justify="flex-end">
            <Select
              size="xs"
              w={240}
              placeholder="Tout mettre en…"
              aria-label={`Tout mettre en (${KIND_LABELS[kind]})`}
              data={styleOptions(webStyles)}
              value={null}
              onChange={(value) => value != null && answerWholeSection(section, value)}
            />
          </Group>
        )}
        {entries.map((entry) => renderEntry(kind, webStyles, entry))}
      </Stack>
    );
  };

  return (
    <Modal
      opened
      onClose={onCancel}
      closeOnClickOutside={false}
      size="90%"
      title="Styles du site"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Choisissez, pour chaque style du document, le style du site qu&apos;il prend.
        </Text>
        {sections.length > 1 ? (
          <Tabs value={activeSection.kind} onChange={setActiveKind}>
            <Tabs.List mb="sm">
              {sections.map(({ kind, entries }) => (
                <Tabs.Tab key={kind} value={kind}>
                  {`${KIND_LABELS[kind]} (${entries.length})`}
                </Tabs.Tab>
              ))}
            </Tabs.List>
            {sections.map((section) => (
              <Tabs.Panel key={section.kind} value={section.kind}>
                {renderSection(section)}
              </Tabs.Panel>
            ))}
          </Tabs>
        ) : activeSection && renderSection(activeSection)}
        <Group
          justify="space-between"
          py="sm"
          bg="white"
          style={{
            position: 'sticky', bottom: 0, zIndex: 1, borderTop: '1px solid var(--mantine-color-gray-2)',
          }}
        >
          {alreadyMappedCount > 0 && questionCount > 0 ? (
            <Switch
              size="xs"
              checked={showAll}
              onChange={(event) => setShowAll(event.currentTarget.checked)}
              label={`Afficher aussi les styles déjà associés (${alreadyMappedCount})`}
            />
          ) : <span />}
          <Group gap="xs">
            <Button variant="subtle" color="gray" onClick={onCancel}>Annuler</Button>
            <Button variant="default" onClick={keepAllDocumentStyles}>
              Tout garder comme dans le document
            </Button>
            <Button onClick={() => onConfirm(answers)}>Valider</Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Rejet de `chooseStyleMapping` quand l'utilisateur annule la modale. */
export class StyleMappingCancelled extends Error {
  constructor() {
    super('Correspondance des styles annulée');
    this.name = 'StyleMappingCancelled';
  }
}

export const isStyleMappingCancelled = (error) => error instanceof StyleMappingCancelled;

/**
 * La modale en promesse, pour `buildWebDataForProposal({ chooseStyleMapping })`.
 * L'appelant rend `modal` quelque part dans son arbre. Annuler rejette avec
 * `StyleMappingCancelled` : la génération s'arrête sans rien écrire.
 */
export function useStyleMappingPrompt() {
  const [pending, setPending] = useState(null);

  const chooseStyleMapping = useCallback((request) => new Promise((resolve, reject) => {
    setPending({ request, resolve, reject });
  }), []);

  const modal = pending ? (
    <StyleMappingModal
      request={pending.request}
      onConfirm={(answers) => {
        pending.resolve(answers);
        setPending(null);
      }}
      onCancel={() => {
        pending.reject(new StyleMappingCancelled());
        setPending(null);
      }}
    />
  ) : null;

  return { chooseStyleMapping, modal };
}
